////////////////////////////////////////////////////////////////////////////////
//
//
// (C) Copyright 2023 Autodesk, Inc. All rights reserved.
//
//                      ****  CONFIDENTIAL MATERIAL  ****
//
// The information contained herein is confidential, proprietary to
// Autodesk, Inc., and considered a trade secret.  Use of this information
// by anyone other than authorized employees of Autodesk, Inc. is granted
// only under a written nondisclosure agreement, expressly prescribing the
// the scope and manner of such use.
//
////////////////////////////////////////////////////////////////////////////////

import React, {ReactNode, useEffect, useReducer} from 'react';
import {useNavigate, useParams} from "react-router";
import {reducer} from "../components/reducers/TaskEditReducer";
import {TaskEditState} from "../components/states/TaskEditState";
import {TaskService} from "../services/TaskService";
import {TaskEditActions, TaskTrigger} from "../Enums";
import {Task} from "../dataModel/Task";
import {
  BlueButton,
  CenteringContainer,
  ContentWrapper,
  FlexColumn,
  FlexRow,
  FlexRowCentered
} from '../CommonStyledComponents';
import {EDIT_TAB_IDS, EDIT_TAB_TITLES, EDIT_TABS, FILE_TAB_IDS, FILE_TABS, PAGES, PATHS} from "../Constants";
import SettingsSource from "../components/SettingsSource";
import SettingsDestination from "../components/SettingsDestination";
import SettingsOptions from "../components/SettingsOptions";
import SettingsSummary from "../components/SettingsSummary";
import {ProjectService} from "../services/ProjectService";
import {
  CompositeExtractType,
  CompositeRenamingType,
  ExtractLocationType,
  FileDestinationNamingType,
  ProjectType,
  StorageType
} from "../clients/Classes";
import {
  FindProjectItemRecursive,
  GetErrorMessage,
  GetNextEditTab,
  GetPreviousEditTab,
  GetValidationPayload,
  IsProjectValidForDestination,
  IsTabDisabled,
  TaskHasCloudModels,
} from "../Utility";
import {FileUI} from "../dataModel/FileUI";
import {ZipTaskSetting} from "../dataModel/ZipTaskSetting";
import {TaskTranslator} from "../dataModel/Translators/TaskTranslator";
import {ProjectUI} from "../dataModel/ProjectUI";
import Theme from "@adsk/alloy-react-theme";
import ProgressRing from "@adsk/alloy-react-progress-ring";
import {ChevronLeftIcon, ChevronRightIcon, PlayIcon, XIcon} from "@adsk/alloy-react-icon";
import Modal from "@adsk/alloy-react-modal";
import Tooltip from "@adsk/alloy-react-tooltip";
import Tabs, {Tab} from "@adsk/alloy-react-tabs";
import {ProjectWiseService} from "../services/ProjectWiseService";
import {ProjectWiseConfigurationTranslator} from "../dataModel/Translators/ProjectWiseConfigurationTranslator";
import {FileService} from "../services/FileService";
import {DirectoryUI} from "../dataModel/DirectoryUI";
import {ProjectWiseConfigurationUI} from "../dataModel/ProjectWiseConfigurationUI";
import {RecurrenceSettings} from "../dataModel/RecurrenceSettings";
import {OptionsState} from "../components/states/OptionsState";
import {BIM360ItemBase} from "../dataModel/BIM360ItemBase";
import {ZipEntryUI} from "../dataModel/ZipEntryUI";

const service = new TaskService();
const projectService = new ProjectService();
const projectWiseService = new ProjectWiseService();
const fileService = new FileService();

const TaskEdit = () => {
  const {id, action} = useParams();
  const [state, dispatch] = useReducer(reducer, new TaskEditState());
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    function setSelectedProjects(task: Task | undefined, projects: ProjectUI[], projectWiseConfigs: ProjectWiseConfigurationUI[], additionalPayload: any): void {
      let projectSource: ProjectUI | undefined;
      let configSource: ProjectWiseConfigurationUI | undefined;
      let projectSystem = StorageType.Forge;
      let destinationSystem = StorageType.Forge;
      if (task == null || (task.Directories.length === 0 && task.Files.length === 0)) {
        projectSource = projects.length > 0 ? projects[0] : undefined;
        configSource = projectWiseConfigs.length > 0 ? projectWiseConfigs[0] : undefined;
      } else {
        const taskBasedId = task.Directories.length > 0
          ? task.Directories[0].ProjectId
          : task.Files[0] instanceof FileUI ? task.Files[0].ProjectId : task.Files[0].File.ProjectId;
        projectSystem = task.Directories.length > 0
          ? task.Directories[0].StorageType
          : task.Files[0] instanceof FileUI ? task.Files[0].StorageType : task.Files[0].File.StorageType;
        configSource = projectWiseConfigs.find(c => c.ApiConfiguration.id === taskBasedId)
          ?? (projectWiseConfigs.length > 0 ? projectWiseConfigs[0] : undefined);
        projectSource = projects.find(proj => proj.Id === taskBasedId)
          ?? (projects.length > 0 ? projects[0] : undefined);
      }

      let projectDestination: ProjectUI | undefined;
      let configDestination: ProjectWiseConfigurationUI | undefined;
      if (task == null || task.ExportDirectory == null) {
        projectDestination = projects.find(IsProjectValidForDestination);
        configDestination = projectWiseConfigs.length > 0 ? projectWiseConfigs[0] : undefined;
      } else {
        const taskBasedId = task.ExportDirectory.ProjectId;
        projectDestination = projects.find(proj => proj.Id === taskBasedId) ?? projects.find(IsProjectValidForDestination);
        configDestination = projectWiseConfigs.find(c => c.ApiConfiguration.id === taskBasedId)
          ?? (projectWiseConfigs.length > 0 ? projectWiseConfigs[0] : undefined);
        destinationSystem = task.ExportDirectory.StorageType;
      }

      dispatch({
        type: TaskEditActions.multipleActions,
        payload: {
          ...additionalPayload,
          selectedProjectSource: projectSource,
          selectedProjectDestination: projectDestination,
          selectedProjectWiseConfigSource: configSource,
          selectedProjectWiseConfigDestination: configDestination,
          selectedTabSource: projectSystem === StorageType.ProjectWise ? FILE_TAB_IDS[FILE_TABS.PROJECTWISE] : FILE_TAB_IDS[FILE_TABS.FORGE],
          selectedTabDestination: destinationSystem === StorageType.ProjectWise ? FILE_TAB_IDS[FILE_TABS.PROJECTWISE] : FILE_TAB_IDS[FILE_TABS.FORGE],
        }
      });
    }

    let taskPayload: {};
    if (id?.toLowerCase() === 'new') {
      const newTask = new Task();
      newTask.ExportLocationType = ExtractLocationType.OtherDirectory;
      state.Task = newTask;

      taskPayload = {
        Task: newTask,
        Loading: false,
        IsNewTask: true,
        IsDuplicating: false
      };
    } else {
      taskPayload = {
        Loading: true,
        IsNewTask: false,
        IsDuplicating: action?.toLowerCase() === 'duplicate'
      };
      service.GetTask(id!)
        .then(task => {
          if (!isMounted) {
            return;
          }

          if (action?.toLowerCase() === 'duplicate') {
            task.Name = `${task.Name} Copy`;
            task.Id = undefined;
          }

          state.Task = task;

          setSelectedProjects(task, state.Projects, state.ProjectWiseConfigs, {
            Task: task,
            Loading: false,
            OriginalTask: task.GetComparisonClone(),
          });
        })
        .catch(error => onError(error, 'Get Task'));
    }

    dispatch({
      type: TaskEditActions.multipleActions,
      payload: {
        ...taskPayload,
        LoadingProjects: true,
        LoadingProjectWiseConfigs: true
      }
    });

    projectService.GetProjects()
      .then(p => {
        if (!isMounted) {
          return;
        }

        state.Projects = p;

        setSelectedProjects(state.Task, p, state.ProjectWiseConfigs, {
          LoadingProjects: false,
          Projects: p,
        });
      })
      .catch(error => onError(error, 'Get Projects'));

    projectWiseService.GetRemainingConfigurations()
      .then(result => {
        const items = result.items!.map(c => ProjectWiseConfigurationTranslator.TranslateConfiguration(c));

        state.ProjectWiseConfigs = items;

        setSelectedProjects(state.Task, state.Projects, items, {
          LoadingProjectWiseConfigs: false,
          ProjectWiseConfigs: items
        });
      })
      .catch(error => onError(error, 'Loading ProjectWise Configurations'));

    return () => {
      isMounted = false;
    }
  }, [action, id]);

  useEffect(() => {
    if (state.NewLoadedProject == null) {
      return;
    }

    const project = state.NewLoadedProject;

    let payload: {
      [k in keyof TaskEditState]?: TaskEditState[k];
    } = {};

    const index = state.Projects.findIndex(x => x.Id === project.Id);
    const newProjects = [...state.Projects];
    newProjects[index] = project;

    payload.Projects = newProjects;

    if (project.Id === state.selectedProjectSource?.Id) {
      payload.selectedProjectSource = project;
    }

    if (project.Id === state.selectedProjectDestination?.Id) {
      payload.selectedProjectDestination = project;

      if (state.Task != null
        && state.Task.CompositeExtractType === CompositeExtractType.ReSaveCloudModel
        && state.HasCloudModels
        && state.selectedProjectSource != null
        && state.selectedProjectSource.ProjectType != ProjectType.ProjectWise) {
        payload.ShowRevitVersionWarning = true;
        if (project.RevitVersionLoadTimeout) {
          payload.RevitVersionTitle = 'Project version not detected';
          payload.RevitVersionWarning = 'The Replication Tool is unable to detect the Revit Model version of your Destination project.  Because of this, any Cloud Models you have in your Source will not remain as Cloud Models in your Destination.';
        } else if (project.RevitVersion == null) {
          payload.RevitVersionTitle = 'Project version will be set';
          payload.RevitVersionWarning = 'The project you selected does not currently have a Revit Cloud Model version set.  Since you have Cloud Models in your Source project, the version of these files will set the version of your Destination project to match.  Once a version is set it cannot be undone.';
        } else if (state.selectedProjectSource.RevitVersion !== project.RevitVersion) {
          payload.RevitVersionTitle = 'Version mismatch';
          payload.RevitVersionWarning = 'The version of the Revit Cloud Model(s) you are copying are different than your Destination project.  These files will not remain Cloud Models once copied.';
        }
      }
    }

    payload.NewLoadedProject = undefined;

    payload = {
      ...payload,
      ...GetValidationPayload(
        state.Task!,
        state.SelectedTab,
        payload.selectedProjectSource ?? state.selectedProjectSource,
        payload.selectedProjectDestination ?? state.selectedProjectDestination,
        getValidationTooltip
      )
    };
    dispatch({type: TaskEditActions.multipleActions, payload: payload});
  }, [state.NewLoadedProject]);

  useEffect(() => {
    if (state.Task == null || !state.LoadingCloudModels) {
      return;
    }

    TaskHasCloudModels(state.Task, fileService)
      .then(result => {
        dispatch({
          type: TaskEditActions.multipleActions,
          payload: {
            LoadingCloudModels: false,
            HasCloudModels: result
          }
        });

        if (state.selectedProjectDestination != null) {
          LoadRevitVersion(state.selectedProjectDestination)
            .catch(error => onError(error, 'Get Destination Revit Version'));
        }
      })
      .catch(error => onError(error, 'Get Cloud Models'));
  }, [state.LoadingCloudModels]);

  useEffect(() => {
    if (state.Task == null) {
      return;
    }

    dispatch({
      type: TaskEditActions.multipleActions,
      payload: GetValidationPayload(
        state.Task,
        state.SelectedTab,
        state.selectedProjectSource,
        state.selectedProjectDestination,
        getValidationTooltip
      )
    });
  }, [state.Task, state.SelectedTab, state.selectedProjectSource, state.selectedProjectDestination]);

  function nextTab(): void {
    let newTab = null;
    switch (state.SelectedTab) {
      case EDIT_TAB_IDS[EDIT_TABS.OPTIONS]:
        newTab = EDIT_TAB_IDS[EDIT_TABS.SOURCE]
        break;
      case EDIT_TAB_IDS[EDIT_TABS.SOURCE]:
        newTab = EDIT_TAB_IDS[EDIT_TABS.DESTINATION]
        break;
      case EDIT_TAB_IDS[EDIT_TABS.DESTINATION]:
        newTab = EDIT_TAB_IDS[EDIT_TABS.SUMMARY]
        break;
    }

    if (newTab == null) {
      return;
    }

    onTabChange(newTab);
  }

  function onTabChange(tabId: string): void {
    let payload: { [key in keyof TaskEditState]?: TaskEditState[key] } = {};
    const retainCloudModels = state.Task?.CompositeExtractType === CompositeExtractType.ReSaveCloudModel;
    payload.SelectedTab = tabId;

    switch (tabId) {
      case EDIT_TAB_IDS[EDIT_TABS.SOURCE]:
        if (retainCloudModels && state.selectedProjectSource != null) {
          LoadRevitVersion(state.selectedProjectSource)
            .catch(error => onError(error, 'Get Source Revit Version'));
        }
        break;
      case EDIT_TAB_IDS[EDIT_TABS.DESTINATION]:
        if (retainCloudModels && state.Task != null) {
          payload.LoadingCloudModels = true;
        }
        break;
    }

    dispatch({type: TaskEditActions.multipleActions, payload: payload});
  }

  function previousTab(): void {
    let newTab = null;
    switch (state.SelectedTab) {
      case EDIT_TAB_IDS[EDIT_TABS.SUMMARY]:
        newTab = EDIT_TAB_IDS[EDIT_TABS.DESTINATION]
        break;
      case EDIT_TAB_IDS[EDIT_TABS.DESTINATION]:
        newTab = EDIT_TAB_IDS[EDIT_TABS.SOURCE]
        break;
      case EDIT_TAB_IDS[EDIT_TABS.SOURCE]:
        newTab = EDIT_TAB_IDS[EDIT_TABS.OPTIONS]
        break;
    }

    if (newTab == null) {
      return;
    }

    onTabChange(newTab);
  }

  function cancel(): void {
    navigate(`${PATHS[PAGES.ROOT]}/${PATHS[PAGES.TASK]}`);
  }

  function save(): void {
    if (state.IsNewTask || state.IsDuplicating) {
      CreateNewTask();
    } else {
      UpdateTask();
    }
  }

  function CreateNewTask(): void {
    if (state.Task == null) {
      return;
    }

    const create = TaskTranslator.GetCreateObject(state.Task);

    dispatch({type: TaskEditActions.Saving, payload: true});
    service.CreateTask(create)
      .then(() => {
        dispatch({type: TaskEditActions.Saving, payload: false});
        navigate(`${PATHS[PAGES.ROOT]}/${PATHS[PAGES.TASK]}`);
      })
      .catch(error => {
        dispatch({type: TaskEditActions.Saving, payload: false});
        onError(error, 'Save task');
      });
  }

  function UpdateTask(): void {
    if (state.Task == null || state.OriginalTask == null) {
      return;
    }
    const update = TaskTranslator.GetTaskUpdate(state.OriginalTask, state.Task);

    dispatch({type: TaskEditActions.Saving, payload: true});
    service.UpdateTask(state.Task, update).then(newTask => {
      dispatch({
        type: TaskEditActions.multipleActions, payload: {
          Saving: false,
          Task: newTask,
          OriginalTask: newTask.GetComparisonClone()
        }
      });

      navigate(`${PATHS[PAGES.ROOT]}/${PATHS[PAGES.TASK]}`);
    })
      .catch(error => {
        dispatch({type: TaskEditActions.Saving, payload: false});
        onError(error, 'Save task');
      });
  }

  function getValidationTooltip(messages: string[], isTab: boolean): ReactNode | undefined {
    return messages.length > 0
      ? <FlexColumn>
        <div>{
          isTab
            ? 'You can not navigate tabs until the following are resolved:'
            : 'Your task is not complete and can\'t be saved until the following are resolved:'}</div>
        <br/>
        {messages.map(m => <div>{m}</div>)}
      </FlexColumn>
      : undefined;
  }

  function startCancel(): void {
    dispatch({type: TaskEditActions.showCancelConfirm, payload: true});
  }

  function getRootDirectories(root: ProjectUI | ProjectWiseConfigurationUI): Promise<DirectoryUI[]> {
    return root instanceof ProjectUI
      ? fileService.GetRootDirectories(root).then(() => root.RootFolderArray)
      : fileService.GetProjectWiseRootDirectories(root).then(() => root.RootFolderArray);
  }

  function onError(error: any, operation: string): void {
    alert(GetErrorMessage(error, operation));
  }

  function onOptionsChanged(property: keyof OptionsState, value: OptionsState[keyof OptionsState]): void {
    if (state.Task == null) {
      return;
    }

    switch (property) {
      case "name":
        state.Task.Name = value as string;
        break;
      case "description":
        state.Task.Description = value as string;
        break;
      case 'startDate':
        state.Task.StartDate = value as Date;
        break;
      case 'schedule':
        state.Task.RecurrenceSettings = value as RecurrenceSettings;
        break;
      case "naming":
        state.Task.ExportDestinationNaming = value as FileDestinationNamingType ?? FileDestinationNamingType.None;
        break;
      case "email":
        state.Task.EmailOnCompletion = value === true;
        break;
      case "exportCsv":
        state.Task.AttachCsv = value === true;
        break;
      case "maintainFolderStructure":
        state.Task.DoNotMaintainFolderStructure = value !== true;
        break;
      case "retainCloudModel":
        state.Task.CompositeExtractType = value === true
          ? CompositeExtractType.ReSaveCloudModel
          : CompositeExtractType.ExtractAll;
        break;
      case "renameExisting":
        state.Task.RenameType = value === true
          ? CompositeRenamingType.RenameExisting
          : CompositeRenamingType.RenameCopied;
        break;
    }

    dispatch({type: TaskEditActions.task, payload: {...state.Task}});
  }

  function onTriggerChanged(t: TaskTrigger | undefined, d: Date | undefined, r: RecurrenceSettings | undefined): void {
    state.Task!.Trigger = t;
    if (d != null) {
      state.Task!.StartDate = d;
    }
    if (r != null) {
      state.Task!.RecurrenceSettings = r;
    }
    if (t === 'Recurring') {
      state.Task!.RecurrenceSettings = new RecurrenceSettings(new Date(), -1);
    }
    dispatch({type: TaskEditActions.task, payload: {...state.Task}});
  }

  function fileSelectionsChanged(currentSelection: { item: BIM360ItemBase, newIsSelected: boolean }[]): void {
    if (state.Task == null) {
      return;
    }

    if (state.Task.Files.length > 0 || state.Task.Directories.length > 0) {
      const baseObject = state.Task.Files.length > 0 ? state.Task.Files[0] : state.Task.Directories[0];
      const previousSystem = baseObject instanceof ZipTaskSetting ? baseObject.File.StorageType : baseObject.StorageType;
      const previousProject = baseObject instanceof ZipTaskSetting ? baseObject.File.ProjectId : baseObject.ProjectId;

      const newObject = currentSelection[0].item;
      const newSystem = newObject.StorageType;
      const newProject = newObject.ProjectId;

      if (previousSystem !== newSystem || previousProject !== newProject) {
        state.Task.Directories = [];
        state.Task.Files = []
      }
    }

    currentSelection.forEach(s => {
      if (state.Task == null) {
        return;
      }
      if (s.item instanceof DirectoryUI) {
        const index = state.Task.Directories.findIndex(dir => dir.Id === s.item.Id);
        if (s.newIsSelected) {
          if (index < 0) {
            state.Task.Directories.push(s.item);
          } else {
            state.Task.Directories[index] = s.item;
          }
        } else {
          if (index >= 0) {
            state.Task.Directories.splice(index, 1);
          }
        }
      } else if (s.item instanceof FileUI) {
        const existing = state.Task.Files.find(f => f instanceof FileUI && f.Id === s.item.Id);

        if (s.newIsSelected && existing == null) {
          state.Task.Files.push(s.item);
        } else if (!s.newIsSelected && existing != null) {
          state.Task.Files.splice(state.Task.Files.indexOf(existing), 1);
        }
      } else if (s.item instanceof ZipEntryUI) {
        const existingFile = state.Task.Files
          .find(f => f instanceof ZipTaskSetting && f.File.Id === s.item.ParentId);

        if (s.newIsSelected) {
          if (existingFile == null) {
            const file = FindProjectItemRecursive(state.selectedProjectSource!.RootFolderArray, s.item.ParentId!) as FileUI;
            const zip = new ZipTaskSetting(file);
            zip.Zips.push(s.item as ZipEntryUI);
            state.Task.Files.push(zip);
          } else {
            const existingZip = (existingFile as ZipTaskSetting).Zips.find(z => z.Name === s.item.Name);
            if (existingZip == null) {
              (existingFile as ZipTaskSetting).Zips.push(s.item as ZipEntryUI);
            }
          }
        } else if (existingFile != null && existingFile instanceof ZipTaskSetting) {
          const existingZip = existingFile.Zips.find(z => z.Name === s.item.Name);
          if (existingZip != null) {
            if (existingFile.Zips.length === 1) {
              state.Task.Files.splice(state.Task.Files.indexOf(existingFile));
            } else {
              existingFile.Zips.splice(existingFile.Zips.indexOf(existingZip), 1);
            }
          }
        }
      }
    });

    state.Task.Archive = false;
    dispatch({type: TaskEditActions.task, payload: {...state.Task}});
  }

  async function LoadRevitVersion(project: ProjectUI): Promise<void> {
    if (project.RevitVersionLoading) {
      return;
    }

    const supportedProjectTypes = [
      ProjectType.ACC,
      ProjectType.B360Docs
    ]

    if (!supportedProjectTypes.includes(project.ProjectType)) {
      project.RevitVersionLoaded = true;
      project.RevitVersion = 'Cloud Models Unsupported';
    }

    try {
      if (!project.RevitVersionLoaded) {
        project.RevitVersionLoading = true;
        let action: TaskEditActions | undefined;
        if (project.Id === state.selectedProjectSource?.Id) {
          action = TaskEditActions.selectedProjectSource;
        }
        if (project.Id === state.selectedProjectDestination?.Id) {
          action = TaskEditActions.selectedProjectDestination;
        }
        if (action != null) {
          dispatch({type: action, payload: project});
        }

        const result = await projectService.GetProjectRevitVersion(project.HubId, project.Id);

        project.RevitVersionLoading = false;
        project.RevitVersionLoaded = true;
        project.RevitVersion = result.revitProjectVersion;
        project.RevitVersionLoadTimeout = result.searchMaxPages === true;
      }

      dispatch({type: TaskEditActions.newLoadedProject, payload: project});
    } catch (error) {
      onError(error, 'Load Revit Version');
    }
  }

  return (
    <ContentWrapper>
      {
        (state.Loading || state.Saving) &&
        <CenteringContainer style={{flex: 1}}>
          <ProgressRing size={'large'}/>
        </CenteringContainer>
      }
      {state.Task != null && !state.Loading && !state.Saving && (
        <>
          <h1
            style={Theme.typography.heading1}>{state.IsNewTask ? 'New Task of Files & Folders' : 'Edit Task of Files & Folders'}</h1>
          <FlexRow style={{marginBottom: '2em', marginTop: '1em', flex: 0}}>
            <Tooltip content={state.SaveTooltip}>
              <BlueButton onClick={save}
                          disabled={state.DisableSave}
                          style={{marginRight: '2em'}}>
                <FlexRowCentered>
                  <PlayIcon style={{marginRight: '0.5em'}}/>
                  <span style={Theme.typography.labelMedium}>Save and run</span>
                </FlexRowCentered>
              </BlueButton>
            </Tooltip>
            <Tooltip content={state.PreviousTooltip}>
              <BlueButton onClick={previousTab}
                          disabled={state.DisablePrevious}
                          style={{marginRight: '2em'}}>
                <FlexRowCentered>
                  <ChevronLeftIcon style={{marginRight: '0.5em'}}/>
                  <span style={Theme.typography.labelMedium}>Back</span>
                </FlexRowCentered>
              </BlueButton>
            </Tooltip>
            <Tooltip content={state.NextTooltip}>
              <BlueButton onClick={nextTab}
                          disabled={state.DisableNext}
                          style={{marginRight: '2em'}}>
                <FlexRowCentered>
                  <ChevronRightIcon style={{marginRight: '0.5em'}}/>
                  <span style={Theme.typography.labelMedium}>Next</span>
                </FlexRowCentered>
              </BlueButton>
            </Tooltip>
            <BlueButton onClick={startCancel}>
              <FlexRowCentered>
                <XIcon style={{marginRight: '0.5em'}}/>
                <span style={Theme.typography.labelMedium}>Cancel</span>
              </FlexRowCentered>
            </BlueButton>
          </FlexRow>
          <Tabs active={state.SelectedTab}
                style={{flex: 1, display: 'flex', flexDirection: "column", minHeight: '0'}}
                onChange={onTabChange}>
            {Object.keys(EDIT_TABS).map((k) => {
              return (
                <Tab label={EDIT_TAB_TITLES[k]}
                     tab={EDIT_TAB_IDS[k]}
                     key={EDIT_TAB_IDS[k]}
                     disabled={
                       IsTabDisabled(EDIT_TAB_IDS[k], state.SelectedTab)
                       || (EDIT_TAB_IDS[k] === GetNextEditTab(state.SelectedTab) && state.DisableNext)
                       || (EDIT_TAB_IDS[k] === GetPreviousEditTab(state.SelectedTab) && state.DisablePrevious)}
                     style={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: '0', overflow: 'auto'}}>
                  <FlexColumn style={{paddingTop: '1em'}}>
                    {state.SelectedTab === EDIT_TAB_IDS[EDIT_TABS.OPTIONS] &&
                      <SettingsOptions
                        name={state.Task?.Name ?? ''}
                        description={state.Task?.Description ?? ''}
                        taskTrigger={state.Task?.Trigger}
                        recurrenceSettings={state.Task?.RecurrenceSettings}
                        startTime={state.Task?.StartDate}
                        allowRecurring={true}
                        namingType={state.Task?.ExportDestinationNaming}
                        emailOnCompletion={state.Task?.EmailOnCompletion ?? false}
                        attachCsv={state.Task?.AttachCsv ?? false}
                        maintainFolderStructure={state.Task?.DoNotMaintainFolderStructure !== true}
                        retainCloudModel={state.Task?.CompositeExtractType === CompositeExtractType.ReSaveCloudModel ?? true}
                        renameExisting={state.Task?.RenameType === CompositeRenamingType.RenameExisting}
                        onChanged={onOptionsChanged}
                        onTriggerChanged={onTriggerChanged}
                      />}
                    {state.SelectedTab === EDIT_TAB_IDS[EDIT_TABS.SOURCE] &&
                      <SettingsSource
                        projects={state.Projects}
                        loadingProjects={state.LoadingProjects}
                        getRootDirectories={getRootDirectories}
                        retainCloudModels={state.Task?.CompositeExtractType === CompositeExtractType.ReSaveCloudModel ?? true}
                        files={state.Task?.Files ?? []}
                        directories={state.Task?.Directories ?? []}
                        getDirectoryContents={d => fileService.GetDirectoryContents(d, state.Task?.Directories)}
                        getZipContents={z => fileService.GetZipContents(z)}
                        projectWiseConfigs={state.ProjectWiseConfigs}
                        loadingProjectWiseConfigs={state.LoadingProjectWiseConfigs}
                        selectedProjectWiseConfig={state.selectedProjectWiseConfigSource}
                        expandedIds={state.expandedSource}
                        selectedProject={state.selectedProjectSource}
                        selectedTab={state.selectedTabSource}
                        onTabChange={t => dispatch({type: TaskEditActions.selectedTabSource, payload: t})}
                        onProjectSelected={p => {
                          if (state.Task != null) {
                            state.Task.Directories = [];
                            state.Task.Files = [];
                            state.Task.Archive = p == null || p.ProjectType === ProjectType.A360 || p.ProjectType === ProjectType.B360Team;
                          }
                          dispatch({
                            type: TaskEditActions.multipleActions,
                            payload: {selectedProjectSource: p, Task: {...state.Task}}
                          });
                          if (p != null && state.Task?.CompositeExtractType === CompositeExtractType.ReSaveCloudModel) {
                            LoadRevitVersion(p)
                              .catch(error => onError(error, 'Get Source Revit Version'));
                          }
                        }}
                        onFileSelectionChanged={fileSelectionsChanged}
                        onProjectWiseConfigSelected={p => dispatch({
                          type: TaskEditActions.selectedProjectWiseConfigSource,
                          payload: p
                        })}
                        onExpandedChanged={ids => dispatch({
                          type: TaskEditActions.expandedSource,
                          payload: ids
                        })}
                        onError={onError}/>}
                    {state.SelectedTab === EDIT_TAB_IDS[EDIT_TABS.DESTINATION] &&
                      <SettingsDestination
                        selectedDirectory={state.Task?.ExportDirectory}
                        onSelectedChanged={d => {
                          if (state.Task != null) {
                            state.Task.ExportDirectory = d;
                            dispatch({type: TaskEditActions.task, payload: {...state.Task}});
                          }
                        }}
                        projects={state.Projects.filter(IsProjectValidForDestination)}
                        loadingProjects={state.LoadingProjects}
                        retainCloudModels={state.Task?.CompositeExtractType === CompositeExtractType.ReSaveCloudModel ?? false}
                        hasCloudModels={state.HasCloudModels}
                        loadingCloudModels={state.LoadingCloudModels}
                        getRootDirectories={getRootDirectories}
                        getDirectoryContents={d => fileService.GetDirectoryContents(d)}
                        getZipContents={z => fileService.GetZipContents(z)}
                        projectWiseConfigs={state.ProjectWiseConfigs}
                        loadingProjectWiseConfigs={state.LoadingProjectWiseConfigs}
                        selectedProjectWiseConfig={state.selectedProjectWiseConfigDestination}
                        expandedIds={state.expandedDestination}
                        selectedProject={state.selectedProjectDestination}
                        selectedTab={state.selectedTabDestination}
                        onTabChange={t => dispatch({type: TaskEditActions.selectedTabDestination, payload: t})}
                        onProjectSelected={p => {
                          dispatch({
                            type: TaskEditActions.selectedProjectDestination,
                            payload: p
                          });
                          if (p != null && state.Task?.CompositeExtractType === CompositeExtractType.ReSaveCloudModel) {
                            LoadRevitVersion(p)
                              .catch(error => onError(error, 'Get Destination Revit Version'));
                          }
                        }}
                        onProjectWiseConfigSelected={p => dispatch({
                          type: TaskEditActions.selectedProjectWiseConfigDestination,
                          payload: p
                        })}
                        onExpandedChanged={ids => dispatch({
                          type: TaskEditActions.expandedDestination,
                          payload: ids
                        })}
                        onError={onError}/>}
                    {state.SelectedTab === EDIT_TAB_IDS[EDIT_TABS.SUMMARY] &&
                      <SettingsSummary task={state.Task}
                                       projects={[...state.Projects, ...state.ProjectWiseConfigs]}
                                       projectsLoading={state.LoadingProjects}
                                       onError={onError}/>}
                  </FlexColumn>
                </Tab>
              )
            })}
          </Tabs>
        </>
      )}
      <Modal open={state.showCancelConfirm}>
        <Modal.Header>Lose Changes?</Modal.Header>
        <Modal.Body>
          <p style={Theme.typography.bodyMediumBold}>Canceling will cause you to lose any changes you have made, are you
            sure?</p>
        </Modal.Body>
        <Modal.Footer>
          <FlexRow style={{justifyContent: 'end'}}>
            <BlueButton onClick={cancel}>
              <FlexRowCentered>
                <span style={Theme.typography.labelMedium}>Yes</span>
              </FlexRowCentered>
            </BlueButton>
            <BlueButton style={{marginLeft: '1em'}}
                        onClick={() => dispatch({type: TaskEditActions.showCancelConfirm, payload: false})}>
              <FlexRowCentered>
                <span style={Theme.typography.labelMedium}>No</span>
              </FlexRowCentered>
            </BlueButton>
          </FlexRow>
        </Modal.Footer>
      </Modal>
      <Modal open={state.ShowRevitVersionWarning}>
        <Modal.Header>{state.RevitVersionTitle}</Modal.Header>
        <Modal.Body>
          <p style={Theme.typography.bodyMediumBold}>{state.RevitVersionWarning}</p>
        </Modal.Body>
        <Modal.Footer>
          <FlexRow style={{justifyContent: 'end'}}>
            <BlueButton style={{marginLeft: '1em'}}
                        onClick={() => dispatch({type: TaskEditActions.showRevitVersionWarning, payload: false})}>
              <FlexRowCentered>
                <span style={Theme.typography.labelMedium}>OK</span>
              </FlexRowCentered>
            </BlueButton>
          </FlexRow>
        </Modal.Footer>
      </Modal>
    </ContentWrapper>
  );
};

export default TaskEdit;